import React, { useEffect, useState } from 'react';
import {
	Laboratory,
	LaboratoryRequisition,
	LaboratoryWorkplace,
	useCreateLaboratoryRequisitionWorkplaceMutation,
	useDeleteLaboratoryRequisitionWorkplaceMutation,
	useDeleteLaboratoryWorkplaceMutation,
	useLaboratoryRequestsQuery,
} from '../../../../graphql/graphql';
import styles from './LaboratorySetupItem.module.scss';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import Loading from '../../../../Components/Loading';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
import LaboratoryActivationModal from './LaboratoryActivation/LaboratoryActivationModal';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import LinkButton from '../../../../Components/Button/LinkButton';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';

interface Props {
	item: Laboratory;
	workplaceLaboratory?: LaboratoryWorkplace;
	workplaceId: string;
	onChange: () => void;
}

const LaboratorySetupItem: React.FC<Props> = ({
	item,
	workplaceId,
	onChange,
	workplaceLaboratory,
}) => {
	const allowed = !!workplaceLaboratory;
	const [isActivationWizardOpen, setIsActivationWizardOpen] = useState(false);
	const [isDisableConfirmationOpen, setIsDisableConfirmationOpen] =
		useState(false);
	const [deleteLaboratoryWorkplace] = useDeleteLaboratoryWorkplaceMutation();
	const [createLaboratoryRequisitionWorkplace] =
		useCreateLaboratoryRequisitionWorkplaceMutation();
	const [deleteLaboratoryRequisitionWorkplace] =
		useDeleteLaboratoryRequisitionWorkplaceMutation();
	const { flatData, pagination, getNextPage, appendData, reset } =
		useFlatDataList<LaboratoryRequisition>({}, 30);

	const {
		data,
		isLoading,
		isFetching,
		refetch: reFetchRequests,
	} = useLaboratoryRequestsQuery({
		where: { laboratoryId: item.id },
		pagination,
	});

	useEffect(() => {
		appendData(
			(data?.laboratoryRequisitions?.data as LaboratoryRequisition[]) ?? [],
		);
	}, [data]);

	const refresh = () => {
		reset();
		reFetchRequests();
	};

	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	const handleDisableLaboratory = async () => {
		if (!workplaceLaboratory) return;
		const result = await deleteLaboratoryWorkplace({
			where: {
				id: workplaceLaboratory.id,
			},
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryDisabled'),
			});
		}
		setIsDisableConfirmationOpen(false);
		onChange();
	};

	const handleEnableRequisition = async (id: string) => {
		const result = await createLaboratoryRequisitionWorkplace({
			input: {
				workplaceId,
				laboratoryRequisitionId: id,
			},
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryRequestAllowed'),
			});
		}
		refresh();
	};

	const handleDisableRequisition = async (id: string) => {
		const result = await deleteLaboratoryRequisitionWorkplace({
			input: {
				workplaceId,
				laboratoryRequisitionId: id,
			},
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.laboratorySetup.laboratoryRequestDisabled'),
			});
		}
		refresh();
	};

	const handleToggleLaboratory = async () => {
		if (allowed) {
			setIsDisableConfirmationOpen(true);
		} else {
			setIsActivationWizardOpen(true);
		}
	};

	const handleToggleRequisition = async (id: string, active: boolean) => {
		if (active) {
			await handleDisableRequisition(id);
		} else {
			await handleEnableRequisition(id);
		}
	};

	const handleLaboratoryActivationSuccess = () => {
		setIsActivationWizardOpen(false);
		onChange();
	};

	if (isLoading || isFetching) {
		return <Loading />;
	}

	return (
		<>
			<div className={styles.item}>
				<div className={styles.header}>
					<Checkbox onChange={handleToggleLaboratory} value={allowed} />
					<div className={styles.title}>{item.name}</div>
					{allowed && (
						<div className={styles.controls}>
							<LinkButton
								title='base.settings'
								theme={Theme.BLUE}
								onClick={() => setIsActivationWizardOpen(true)}
								firstUpper
							/>
						</div>
					)}
				</div>
				{allowed && (
					<div className={styles.requisitions}>
						{flatData?.map(requisition => {
							return (
								<div className={styles.requisition}>
									<Checkbox
										onChange={() =>
											handleToggleRequisition(
												requisition.id,
												requisition.allowed!,
											)
										}
										value={requisition.allowed!}
									/>
									<div>{requisition.name}</div>
								</div>
							);
						})}
						{data?.laboratoryRequisitions?.meta?.hasNextPage && (
							<div>
								<LinkButton
									title='base.loadMore'
									theme={Theme.BLUE}
									onClick={getNextPage}
								/>
							</div>
						)}
					</div>
				)}
			</div>
			{isActivationWizardOpen && (
				<LaboratoryActivationModal
					isOpen
					onClose={() => setIsActivationWizardOpen(false)}
					workplaceId={workplaceId}
					laboratory={item}
					workplaceLaboratory={workplaceLaboratory}
					onActivationSuccess={handleLaboratoryActivationSuccess}
				/>
			)}
			{isDisableConfirmationOpen && (
				<ConfirmationModal
					isOpen
					title='component.laboratorySetupItem.disableConfirmation.title'
					text='component.laboratorySetupItem.disableConfirmation.text'
					onClose={() => setIsDisableConfirmationOpen(false)}>
					<div className={styles.buttons}>
						<Button
							theme={Theme.GREEN}
							onClick={handleDisableLaboratory}
							title='base.yes'
						/>
						<LinkButton
							title='base.no'
							theme={Theme.RED}
							onClick={() => setIsDisableConfirmationOpen(false)}
							firstUpper
						/>
					</div>
				</ConfirmationModal>
			)}
		</>
	);
};

export default LaboratorySetupItem;
